import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import './Services.css';

const serviceTypes = [
  {
    id: '360-booth',
    name: '360° Photo Booth',
    description: 'Capture every angle with our premium 360° video platform. Guests step onto our platform while a rotating camera captures stunning slow-motion videos.',
    image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600',
    features: ['Slow-motion video capture', 'Instant social sharing', 'Custom overlays', 'LED lighting effects'],
  },
  {
    id: 'mirror-booth',
    name: 'Mirror Photo Booth',
    description: 'Our interactive magic mirror delivers a unique touchscreen experience with animations, games, and custom overlays that engage your guests.',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600',
    features: ['Interactive touchscreen', 'Voice guidance', 'Signature capture', 'Instant prints'],
  },
  {
    id: 'classic-booth',
    name: 'Classic Photo Booth',
    description: 'The timeless enclosed booth experience with a modern twist. High-quality DSLR photos with professional lighting.',
    image: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=600',
    features: ['Enclosed privacy', 'Professional lighting', 'Instant photo strips', 'Green screen options'],
  },
  {
    id: 'open-air',
    name: 'Open Air Photo Booth',
    description: 'Perfect for large groups and grand backdrops. Our open-air setup accommodates more guests and works with any event theme.',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600',
    features: ['Group photos', 'Custom backdrops', 'DSLR quality', 'Unlimited sessions'],
  },
];

export default function Services() {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [packagesData, addonsData] = await Promise.all([
        api.getPackages(),
        api.getAddons(),
      ]);
      setPackages(packagesData);
      setAddons(addonsData);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="services-page">
      {/* Hero */}
      <section className="services-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">Our Services</span>
            <h1 className="page-title">Premium Photo Booth Experiences</h1>
            <p className="page-description">
              From intimate gatherings to grand celebrations, we have the perfect photo booth solution for your event.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Types */}
      <section className="section">
        <div className="container">
          <div className="services-list">
            {serviceTypes.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                className={`service-item ${index % 2 === 1 ? 'reverse' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="service-image">
                  <img src={service.image} alt={service.name} />
                </div>
                <div className="service-content">
                  <h2 className="service-name">{service.name}</h2>
                  <p className="service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, i) => (
                      <li key={i}>
                        <CheckCircle size={18} className="text-gold" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/book" className="btn btn-primary">
                    Book This Booth
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="section packages-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Pricing</span>
            <h2 className="section-title">Event Packages</h2>
            <p className="section-description">
              Choose from our carefully curated packages or customize your own experience.
            </p>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner" />
            </div>
          ) : (
            <div className="packages-grid">
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  className={`package-card ${pkg.popular ? 'popular' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  {pkg.popular && <span className="package-badge">Most Popular</span>}
                  <h3 className="package-name">{pkg.name}</h3>
                  <div className="package-price">
                    <span className="price-amount">${pkg.price}</span>
                  </div>
                  <div className="package-meta">
                    <span><Clock size={16} /> {pkg.duration}</span>
                    {pkg.booth_type && <span><Users size={16} /> {pkg.booth_type}</span>}
                  </div>
                  <p className="package-description">{pkg.description}</p>
                  <ul className="package-features">
                    {pkg.features?.map((feature, i) => (
                      <li key={i}>
                        <CheckCircle size={16} className="text-gold" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/book" className="btn btn-primary w-full">
                    Select Package
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add-ons */}
      {addons.length > 0 && (
        <section className="section addons-section">
          <div className="container">
            <div className="section-header">
              <span className="section-subtitle">Enhance Your Experience</span>
              <h2 className="section-title">Add-Ons</h2>
            </div>
            <div className="addons-grid">
              {addons.map((addon, index) => (
                <motion.div
                  key={addon.id}
                  className="addon-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="addon-info">
                    <h4 className="addon-name">{addon.name}</h4>
                    <p className="addon-description">{addon.description}</p>
                  </div>
                  <span className="addon-price">${addon.price}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Book?</h2>
            <p className="cta-description">
              Start planning your perfect event today. Our team is here to help!
            </p>
            <Link to="/book" className="btn btn-primary">
              Book Now
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
